import { useState } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import { CheckIcon, StarIcon } from './icons';
import { formatPrice, getLicensesLabel } from './helpers';
import {
    CYCLE_LABEL,
    PLAN_CARD_LABELS,
    FEATURE_BASE,
    FEATURE_GROUPS,
} from './constants';

/**
 * Shared plan card — used by both normal and event-sales layouts.
 *
 * All discount / coupon resolution has already been done by the parent
 * (PlanCards) before this component receives its props. This component is
 * purely presentational.
 *
 * @param {Object}   props
 * @param {string}   props.cardLabel
 * @param {boolean}  props.isFeatured
 * @param {boolean}  props.hasDiscount
 * @param {number}   props.discountVal
 * @param {string}   props.discountType        - 'percentage' | 'fixed'
 * @param {number}   props.displayPrice        - Raw (pre-discount) display price
 * @param {number|null} props.discountedDisplayPrice
 * @param {number|null} props.discountedAnnualPrice
 * @param {Object}   props.entry               - Freemius pricing entry
 * @param {string}   props.billingCycle
 * @param {string}   props.currency
 * @param {boolean}  props.isEventActive
 * @param {boolean}  props.isCheckingOut
 * @param {Function} props.onCheckout
 * @param {boolean}  props.openGroups
 * @param {Function} props.onToggleGroup
 */
export const PlanCard = ({
    cardLabel,
    isFeatured,
    hasDiscount,
    discountVal,
    discountType,
    displayPrice,
    discountedDisplayPrice,
    discountedAnnualPrice,
    entry,
    billingCycle,
    currency,
    isEventActive,
    isCheckingOut,
    onCheckout,
    openGroups,
    onToggleGroup,
}) => (
    <div className={`f-pricing__plan${isFeatured ? ' is-featured' : ''}${isEventActive ? ' is-event-sales' : ''}`}>
        {isFeatured && (
            <div className="f-pricing__badge">
                <StarIcon /> {__('Most Popular', 'jeg-elementor-kit')}
            </div>
        )}
        {hasDiscount && !isEventActive && (
            <div className="f-pricing__discount-badge">
                <div className="f-pricing__discount-main">
                    <span className="f-pricing__discount-num">
                        {discountType === 'fixed' ? formatPrice(discountVal, currency) : discountVal}
                    </span>
                    {discountType !== 'fixed' && (
                        <div className="f-pricing__discount-right">
                            <span className="f-pricing__discount-pct">%</span>
                            <span className="f-pricing__discount-off">{__('OFF', 'jeg-elementor-kit')}</span>
                        </div>
                    )}
                </div>
                <span className="f-pricing__discount-label">{__('Limited Offer', 'jeg-elementor-kit')}</span>
            </div>
        )}
        <div className="f-pricing__plan-inner">
            <h3 className="f-pricing__plan-name">{cardLabel}</h3>

            <div className={`f-pricing__price-block${isEventActive ? ' f-pricing__price-block--event' : ''}`}>
                {isEventActive ? (
                    /* ── Event sales price layout ── */
                    <>
                        <div className="f-pricing__event-price-row">
                            {discountedDisplayPrice && (
                                <span className="f-pricing__original-price">{formatPrice(displayPrice, currency)}</span>
                            )}
                            {hasDiscount && (
                                <div className="f-pricing__save-badge">
                                    {discountType === 'fixed'
                                        ? sprintf(__('Save %s', 'jeg-elementor-kit'), formatPrice(discountVal, currency))
                                        : sprintf(__('Save %s%%', 'jeg-elementor-kit'), discountVal)
                                    }
                                </div>
                            )}
                            <div className="f-pricing__amount-row">
                                <span className="f-pricing__amount">{formatPrice(discountedDisplayPrice ?? displayPrice, currency)}</span>
                                <span className="f-pricing__period">
                                    {billingCycle === 'lifetime' ? __(' one-time', 'jeg-elementor-kit') : __('/mo', 'jeg-elementor-kit')}
                                </span>
                            </div>
                        </div>
                    </>
                ) : (
                    /* ── Normal price layout ── */
                    <>
                        {discountedDisplayPrice && (
                            <span className="f-pricing__original-price">{formatPrice(displayPrice, currency)}</span>
                        )}
                        <div className="f-pricing__amount-row">
                            <span className="f-pricing__amount">{formatPrice(discountedDisplayPrice ?? displayPrice, currency)}</span>
                            <span className="f-pricing__period">
                                {billingCycle === 'lifetime' ? __(' one-time', 'jeg-elementor-kit') : __('/month', 'jeg-elementor-kit')}
                            </span>
                        </div>
                    </>
                )}
                {billingCycle === 'annual' && entry.annual_price && (
                    <p className={`f-pricing__billing-note${discountedAnnualPrice ? ' has-renew' : ''}`}>
                        <span className="f-pricing__billing-note-label">{__('Billed annually.', 'jeg-elementor-kit')}</span>{' '}
                        {sprintf(
                            __('Pay %s/year today.', 'jeg-elementor-kit'),
                            formatPrice(discountedAnnualPrice ?? entry.annual_price, currency)
                        )}
                        {discountedAnnualPrice && (
                            <>
                                <br />
                                {sprintf(__('Renew at regular rate %s/year.', 'jeg-elementor-kit'), formatPrice(entry.annual_price, currency))}
                            </>
                        )}
                    </p>
                )}
            </div>

            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className="f-pricing__cta" onClick={onCheckout}>
                {isCheckingOut ? __('Loading...', 'jeg-elementor-kit') : __('Upgrade Now', 'jeg-elementor-kit')}
                <span className="f-pricing__cta-arrow">›</span>
            </a>

            <ul className="f-pricing__features">
                <li>
                    <div className="f-pricing__feature-icon"><CheckIcon /></div>
                    {(() => {
                        const label = sprintf(__('%s Licenses', 'jeg-elementor-kit'), getLicensesLabel(entry.licenses));
                        const m = label.match(/^(\d+\+?)(.*)/);
                        if (m) return <span className="f-pricing__feature-text" data-num={m[1]}>{m[2]}</span>;
                        return <span className="f-pricing__feature-text">{label}</span>;
                    })()}
                </li>
                {FEATURE_BASE.map((feat) => (
                    <li key={feat}>
                        <div className="f-pricing__feature-icon"><CheckIcon /></div>
                        {(() => {
                            const m = feat.match(/^(\d+\+?)(\s*)(.*)/);
                            if (m) return <span className="f-pricing__feature-text" data-num={m[1]}>{m[3]}</span>;
                            return <span className="f-pricing__feature-text">{feat}</span>;
                        })()}
                    </li>
                ))}
            </ul>

            {FEATURE_GROUPS.map((group) => {
                const isOpen = !!openGroups[group.label];
                return (
                    <div key={group.label} className="f-pricing__group">
                        <button
                            className={`f-pricing__group-toggle${isOpen ? ' is-open' : ''}`}
                            onClick={() => onToggleGroup(group.label)}
                        >
                            <span>{group.label}</span>
                            {isOpen ? (
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13.5 11.25L9 6.75L4.5 11.25" stroke="#011627" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="#011627" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </button>
                        {isOpen && group.items.length > 0 && (
                            <ul className="f-pricing__group-items">
                                {group.items.map((item) => (
                                    <li key={item}>
                                        <div className="f-pricing__feature-icon"><CheckIcon /></div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                );
            })}
        </div>
    </div>
);

// Order determines render order of cards.
const SERVER_PLAN_KEYS = ['basic', 'professional', 'agency'];

/**
 * Plan grid — normal layout.
 *
 * Plans and their order are determined by the server (activePlan).
 * Freemius data is used only to resolve prices via plan_id / pricing_id.
 *
 * @param {Object}      props
 * @param {Array}       props.visiblePlans     - All non-hidden Freemius plans
 * @param {string}      props.billingCycle
 * @param {string}      props.currency
 * @param {Object}      props.activePlan       - pricingPlan from server
 * @param {number|null} props.checkingOut
 * @param {Function}    props.onCheckout
 * @param {Object}      props.openGroups
 * @param {Function}    props.onToggleGroup
 */
const NormalPlanCards = ({
    visiblePlans,
    billingCycle,
    currency,
    activePlan,
    checkingOut,
    onCheckout,
    openGroups,
    onToggleGroup,
}) => {
    const cards = SERVER_PLAN_KEYS.map((key) => {
        const serverPlan = activePlan?.[key];
        if (!serverPlan?.plan_id) return null;

        // Match Freemius plan by plan_id from server.
        const fsPlan = visiblePlans.find((p) => String(p.id) === String(serverPlan.plan_id));
        if (!fsPlan) return null;

        // Match pricing entry by pricing_id from server.
        const entry = fsPlan.pricing?.find(
            (p) => String(p.id) === String(serverPlan.pricing_id) && p.currency === currency
        );
        if (!entry) return null;

        const rawPrice = billingCycle === 'annual' ? entry.annual_price
            : billingCycle === 'monthly' ? entry.monthly_price
                : entry.lifetime_price;
        if (rawPrice == null) return null;

        const displayPrice = billingCycle === 'annual' ? rawPrice / 12 : rawPrice;
        const isFeatured = key === 'professional';
        const discountVal = Number(serverPlan.discount) || 0;
        const discountType = serverPlan.discount_type || 'percentage';
        const hasDiscount = discountVal > 0;
        const applyDiscount = (price) => {
            if (!hasDiscount) return null;
            return discountType === 'fixed'
                ? Math.max(0, price - discountVal)
                : price * (1 - discountVal / 100);
        };

        return (
            <PlanCard
                key={entry.id}
                cardLabel={PLAN_CARD_LABELS[key]}
                isFeatured={isFeatured}
                hasDiscount={hasDiscount}
                discountVal={discountVal}
                discountType={discountType}
                displayPrice={displayPrice}
                discountedDisplayPrice={applyDiscount(displayPrice)}
                discountedAnnualPrice={entry.annual_price ? applyDiscount(entry.annual_price) : null}
                entry={entry}
                billingCycle={billingCycle}
                currency={currency}
                isCheckingOut={checkingOut === entry.id}
                onCheckout={(e) => { e.preventDefault(); onCheckout(fsPlan, entry); }}
                isEventActive={false}
                openGroups={openGroups}
                onToggleGroup={onToggleGroup}
            />
        );
    }).filter(Boolean);

    return <div className="f-pricing__plans">{cards}</div>;
};

/**
 * Plan grid — event sales layout.
 * Uses the same card component with isEventActive=true for the different price design.
 *
 * @param {Object} props - same shape as NormalPlanCards
 */
const EventSalesPlanCards = ({
    visiblePlans,
    billingCycle,
    currency,
    activePlan,
    checkingOut,
    onCheckout,
    openGroups,
    onToggleGroup,
}) => {
    const cards = SERVER_PLAN_KEYS.map((key) => {
        const serverPlan = activePlan?.[key];
        if (!serverPlan?.plan_id) return null;

        const fsPlan = visiblePlans.find((p) => String(p.id) === String(serverPlan.plan_id));
        if (!fsPlan) return null;

        const entry = fsPlan.pricing?.find(
            (p) => String(p.id) === String(serverPlan.pricing_id) && p.currency === currency
        );
        if (!entry) return null;

        const rawPrice = billingCycle === 'annual' ? entry.annual_price
            : billingCycle === 'monthly' ? entry.monthly_price
                : entry.lifetime_price;
        if (rawPrice == null) return null;

        const displayPrice = billingCycle === 'annual' ? rawPrice / 12 : rawPrice;
        const isFeatured = key === 'professional';
        const discountVal = Number(serverPlan.discount) || 0;
        const discountType = serverPlan.discount_type || 'percentage';
        const hasDiscount = discountVal > 0;
        const applyDiscount = (price) => {
            if (!hasDiscount) return null;
            return discountType === 'fixed'
                ? Math.max(0, price - discountVal)
                : price * (1 - discountVal / 100);
        };

        return (
            <PlanCard
                key={entry.id}
                cardLabel={PLAN_CARD_LABELS[key]}
                isFeatured={isFeatured}
                hasDiscount={hasDiscount}
                discountVal={discountVal}
                discountType={discountType}
                displayPrice={displayPrice}
                discountedDisplayPrice={applyDiscount(displayPrice)}
                discountedAnnualPrice={entry.annual_price ? applyDiscount(entry.annual_price) : null}
                entry={entry}
                billingCycle={billingCycle}
                currency={currency}
                isCheckingOut={checkingOut === entry.id}
                onCheckout={(e) => { e.preventDefault(); onCheckout(fsPlan, entry); }}
                isEventActive={true}
                openGroups={openGroups}
                onToggleGroup={onToggleGroup}
            />
        );
    }).filter(Boolean);

    return <div className="f-pricing__plans f-pricing__plans--event">{cards}</div>;
};

/**
 * PlanCards — selects the correct layout based on isEventActive.
 *
 * @param {Object}  props
 * @param {boolean} props.isEventActive
 * @param {Array}   props.availableCycles
 * @param {string}  props.billingCycle
 * @param {Function} props.onCycleChange
 * @param {Array}   props.visiblePlans
 * @param {string}  props.currency
 * @param {Object}  props.activePlan
 * @param {number|null} props.checkingOut
 * @param {Function} props.onCheckout
 */
const PlanCards = ({
    isEventActive,
    availableCycles,
    billingCycle,
    onCycleChange,
    visiblePlans,
    currency,
    activePlan,
    checkingOut,
    onCheckout,
}) => {
    const [openGroups, setOpenGroups] = useState({ 'Advance Multipurpose Widget': true });

    const onToggleGroup = (label) =>
        setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

    const sharedProps = {
        visiblePlans,
        billingCycle,
        currency,
        activePlan,
        checkingOut,
        onCheckout,
        openGroups,
        onToggleGroup,
    };

    return (
        <>
            {/* Billing cycle toggle */}
            {availableCycles.length > 1 && (
                <div className="f-pricing__cycles">
                    {availableCycles.map((cycle) => (
                        <button
                            key={cycle}
                            onClick={() => onCycleChange(cycle)}
                            className={`f-pricing__cycle-btn${billingCycle === cycle ? ' is-active' : ''}`}
                        >
                            {CYCLE_LABEL[cycle]}
                        </button>
                    ))}
                </div>
            )}

            {isEventActive
                ? <EventSalesPlanCards {...sharedProps} />
                : <NormalPlanCards {...sharedProps} />
            }
        </>
    );
};

export default PlanCards;
